import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { ThirdwebProvider } from "@thirdweb-dev/react";
import { MintModal } from "@/components/MintModal";
import { HeroSection } from "@/components/HeroSection";
import { ChainSelection } from "@/components/ChainSelection";
import { PersonalitiesCarousel } from "@/components/PersonalitiesCarousel";
import { supportedChains, base, pulsechain } from "@/config/chains";
import { Routes, Route, Link, useLocation } from "react-router-dom";
import { MINT_START_DATE } from "@/config/mint";
import Documentation from "./Documentation";
import FAQ from "./FAQ";
import Support from "./Support";
import License from "./License";
import PrivacyPolicy from "./PrivacyPolicy";
import CookiePolicy from "./CookiePolicy";
import { RarityReportHero } from "@/components/RarityReportHero";

const Index = () => {
  const [selectedChain, setSelectedChain] = useState<string | null>(null);
  const [mintModalOpen, setMintModalOpen] = useState(false);
  const [isPastMintDate, setIsPastMintDate] = useState(new Date() > new Date(MINT_START_DATE));
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { toast } = useToast();
  const location = useLocation();
  const wallpaperUrl = '/brand-images/wallpaper-photos.jpeg';
  const logoUrl = `${import.meta.env.BASE_URL}logo.png`;

  // Close menu when route changes
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  // Scroll to top when route changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Check mint date
  useEffect(() => {
    setIsPastMintDate(new Date() > new Date(MINT_START_DATE));
    const timer = setInterval(() => {
      const now = new Date();
      const mintDate = new Date(MINT_START_DATE);
      if (now > mintDate) {
        setIsPastMintDate(true);
        clearInterval(timer);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const handleChainSelect = (chainId: string) => {
    setSelectedChain(chainId);
    if (chainId === "chia") {
      window.open("https://chia-nft-marketplace.com/cryptfluencers", "_blank");
    } else if (chainId === "pulsechain") {
      window.open("https://hexoacoin.com/mint/?clientId=3e5888d69bd874e256e5800546dbbe80&chainId=369&contract=0xAD010CA83aCF225b4D11109ef925f120A37de7e9", "_blank");
    } else if (chainId === "base") {
      window.open("https://hexoacoin.com/mint/?clientId=3e5888d69bd874e256e5800546dbbe80&chainId=8453&contract=0x07e9324b757F1846a1c4f17832757eb64D6Eb6aD", "_blank");
    } else {
      setMintModalOpen(true);
    }
  };

  const scrollToMint = () => {
    const mintSection = document.getElementById('mint-section');
    if (mintSection) {
      mintSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const getActiveChain = () => {
    switch(selectedChain) {
      case "base":
        return base;
      case "pulsechain":
        return pulsechain;
      default:
        return base;
    }
  };

  const menuLinks = [
    { name: "Mint NFT", path: "/" },
    { name: "Documentation", path: "/documentation" },
    { 
      name: "Rarity Report", 
      path: "/report"
    },
    { name: "FAQ", path: "/faq" },
    { name: "Support", path: "/support" },
    { name: "License", path: "/license" },
    { name: "Privacy Policy", path: "/privacy" },
    { name: "Cookie Policy", path: "/cookies" }
  ];

  const socialLinks = [
    { name: "Twitter", url: "https://x.com/cryptfluencers", icon: "twitter" },
  ];

  const HomePage = () => (
    <>
      <HeroSection isPastMintDate={isPastMintDate} />
      <PersonalitiesCarousel />
      <div className="container mx-auto px-4">
        <ChainSelection 
          selectedChain={selectedChain}
          onChainSelect={handleChainSelect}
          isPastMintDate={isPastMintDate}
        />
        {selectedChain && selectedChain !== "chia" && (
          <MintModal
            open={mintModalOpen}
            onOpenChange={setMintModalOpen}
            selectedChain={selectedChain}
          />
        )}
      </div>
      <RarityReportHero />
    </>
  );

  return (
    <ThirdwebProvider 
      clientId="d85407b5c1c3489abbf510779f640163"
      activeChain={getActiveChain()}
      supportedChains={supportedChains}
      autoConnect={true}
      dAppMeta={{
        name: "Cryptfluencers",
        description: "The ultimate NFT collection featuring the most notorious figures in crypto history.",
        logoUrl: logoUrl,
        url: window.location.origin,
      }}
    >
      <div className="min-h-screen bg-gradient-to-b from-nft-dark via-black to-nft-dark text-white relative">
        <div 
          className="absolute inset-0" 
          style={{ 
            backgroundImage: `url(${wallpaperUrl})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            opacity: 0.7,
            imageRendering: 'pixelated',
            filter: 'contrast(1.1) saturate(1.2)',
            mixBlendMode: 'multiply',
          }} 
        />
        
        <div 
          className="absolute inset-0 bg-[#9333EA]/20"
          style={{ mixBlendMode: 'color-dodge' }}
        />
        
        <nav className="fixed top-0 w-full z-50 backdrop-blur-xl border-b border-white/10">
          <div className="container mx-auto px-4 py-4 flex justify-between items-center">
            <Link to="/"
              className="text-lg md:text-2xl font-pixel text-[#9333EA] hover:opacity-90 transition-opacity cursor-pointer"
            >
              Cryptfluencers
            </Link>
            <div className="flex items-center gap-2">
              <Button 
                onClick={scrollToMint}
                className="bg-[#9333EA] hover:bg-[#9333EA]/80 text-white text-sm px-4 py-2"
              >
                Mint
              </Button>
              <Button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                variant="ghost"
              >
                <div className="space-y-1.5">
                  <div className="w-5 h-0.5 bg-current"></div>
                  <div className="w-5 h-0.5 bg-current"></div>
                  <div className="w-5 h-0.5 bg-current"></div>
                </div>
              </Button>
            </div>
          </div>

          <AnimatePresence>
            {isMenuOpen && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="absolute top-full left-0 w-full bg-black/95 backdrop-blur-xl border-b border-white/10"
              >
                <div className="container mx-auto py-4 px-4">
                  <div className="grid grid-cols-1 gap-2">
                    {menuLinks.map((link) => (
                      <Link
                        key={link.path}
                        to={link.path}
                        className="text-sm text-white/80 hover:text-[#9333EA] hover:bg-white/10 px-4 py-2 rounded-lg transition-colors"
                      >
                        {link.name}
                      </Link>
                    ))}
                    <div className="h-px bg-white/10 my-2" />
                    <div className="px-4 py-2">
                      <p className="text-sm text-white/60 mb-2">Follow Us</p>
                      {socialLinks.map((link) => (
                        <a
                          key={link.name}
                          href={link.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 text-sm text-white/80 hover:text-white hover:bg-white/10 px-4 py-2 rounded-lg transition-colors"
                        >
                          <span>{link.name}</span>
                          <span className="text-xs text-white/60">↗</span>
                        </a>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </nav>

        <main className="relative z-10">
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/documentation" element={<Documentation />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/support" element={<Support />} />
            <Route path="/license" element={<License />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/cookies" element={<CookiePolicy />} />
          </Routes>
        </main>
      </div>
    </ThirdwebProvider>
  );
};

export default Index;